/**
 * 표 페이지 공용 검색바.
 *
 * 동작:
 *   - 텍스트 입력 → 내부 상태에만 보관 (외부 fetch 트리거 X)
 *   - Enter 또는 [검색] 버튼 → onCommit(value) — 외부 fetch 트리거
 *   - 우측 X 버튼 → 즉시 '' commit + 전체 목록 복귀
 *   - 부모 value 외부에서 변경되면 동기화 (예: 초기화 버튼)
 *
 * 자동완성 (옵션):
 *   - suggestions prop 으로 후보 배열 전달 (현재 화면 표 데이터에서 추출)
 *   - 입력 시 매칭 후보를 드롭다운으로 표시
 *   - ↑/↓ 화살표로 이동, Enter 로 선택, Esc 로 닫기, 클릭으로 선택
 *   - 후보 미전달 시 자동완성 비활성 (기존 동작 유지)
 *
 * 디자인:
 *   - 영업진행/매입/재고/비상벨/유지보수/변경이력 등 모든 표 페이지에서 동일.
 *   - placeholder 만 페이지별로 다름.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';

export interface SearchBarProps {
  /** 부모가 보유한 commit 된 검색어 (Enter/X 등으로 확정된 값) */
  value: string;
  /** 검색 적용 콜백 (Enter / [검색] 클릭 / X 클릭). 빈 문자열이면 전체 목록 */
  onCommit: (q: string) => void;
  placeholder?: string;
  /** 추가 wrapper className (grid 셀 등) */
  className?: string;
  /** 입력 박스 너비 — 기본 flex-1, full-width form 에서 자동 채움 */
  inputClassName?: string;
  /**
   * live=true : 매 입력마다 즉시 onCommit (클라 필터링 페이지용). [검색] 버튼 hide.
   * live=false (default) : Enter / [검색] 버튼으로만 onCommit (서버 fetch 페이지용).
   */
  live?: boolean;
  /**
   * 자동완성 후보 배열 — 보통 현재 화면 표 데이터에서 추출한 유니크 문자열.
   * 미전달 또는 빈 배열이면 자동완성 비활성.
   */
  suggestions?: string[];
  /** 드롭다운 최대 표시 개수 (기본 8). */
  maxSuggestions?: number;
}

/** 후보 매칭 — 사용자 입력을 포함하는 문자열만, 시작-일치 우선 */
function filterSuggestions(
  candidates: string[],
  q: string,
  limit: number,
): string[] {
  if (q === '' || candidates.length === 0) return [];
  const lower = q.toLowerCase();
  const starts: string[] = [];
  const contains: string[] = [];
  for (const c of candidates) {
    if (!c) continue;
    const lc = c.toLowerCase();
    if (lc === lower) continue; // 이미 같은 텍스트는 후보로 의미 없음
    if (lc.startsWith(lower)) starts.push(c);
    else if (lc.includes(lower)) contains.push(c);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

export function SearchBar({
  value,
  onCommit,
  placeholder = '🔍 검색',
  className,
  inputClassName,
  live = false,
  suggestions,
  maxSuggestions = 8,
}: SearchBarProps) {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  // 외부 value 변경 시 동기화 (초기화 버튼 등에서 부모가 '' 으로 바꾸면 입력창도 비움)
  useEffect(() => {
    setText(value);
  }, [value]);

  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
  const matched = useMemo<string[]>(
    () =>
      hasSuggestions
        ? filterSuggestions(suggestions!, text.trim(), maxSuggestions)
        : [],
    [hasSuggestions, suggestions, text, maxSuggestions],
  );

  // 입력이 비거나 매칭 후보가 0개면 드롭다운 닫기
  useEffect(() => {
    if (text.trim() === '' || matched.length === 0) {
      setOpen(false);
      setActiveIdx(-1);
    }
  }, [text, matched.length]);

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function submit(q: string = text) {
    setOpen(false);
    setActiveIdx(-1);
    onCommit(q.trim());
  }

  function clear() {
    setText('');
    setOpen(false);
    setActiveIdx(-1);
    onCommit('');
  }

  function pickSuggestion(s: string) {
    setText(s);
    submit(s);
  }

  function handleChange(next: string) {
    setText(next);
    if (hasSuggestions) setOpen(true);
    setActiveIdx(-1);
    if (live) onCommit(next);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // Enter 키 — 활성 후보 있으면 그것을, 없으면 현재 입력값을 commit.
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (open && activeIdx >= 0 && activeIdx < matched.length) {
        pickSuggestion(matched[activeIdx]!);
      } else {
        submit();
      }
      return;
    }
    if (e.key === 'Escape') {
      // 목록을 닫거나 글자를 지울 때만 ESC 를 쓴다 — 쓸 게 없으면 그대로 두어 보기 창이 닫히게 (v2.231.0 · H-4)
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActiveIdx(-1);
      } else if (text.length > 0) {
        e.preventDefault();
        clear();
      }
      return;
    }
    if (!open || matched.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % matched.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? matched.length - 1 : i - 1));
    }
  }

  // form 으로 감싸지 않음 — 부모 페이지가 자체 form / grid 안에 SearchBar 를 끼울 때
  // nested form 충돌 방지. Enter 는 onKeyDown 으로 처리.
  return (
    <div ref={wrapRef} className={`relative flex items-center gap-2 ${className ?? ''}`}>
      <div className="relative flex-1">
        <input
          type="text"
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (hasSuggestions && text.trim() !== '' && matched.length > 0) {
              setOpen(true);
            }
          }}
          placeholder={placeholder}
          className={
            inputClassName ??
            'w-full border border-slate-300 rounded px-3 py-1.5 pr-8 text-sm outline-none focus:border-blue-500'
          }
          autoComplete="off"
        />
        {text.length > 0 && (
          <button
            type="button"
            onClick={clear}
            tabIndex={-1}
            aria-label="검색어 지우기"
            title="검색어 지우기 (Esc)"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 text-xs leading-none flex items-center justify-center"
          >
            ×
          </button>
        )}

        {/* 자동완성 드롭다운 */}
        {open && matched.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-40 left-0 right-0 top-full mt-1 bg-white border border-slate-300 rounded shadow-lg max-h-72 overflow-y-auto text-sm"
          >
            {matched.map((s, i) => {
              const active = i === activeIdx;
              return (
                <li
                  key={`${s}-${i}`}
                  role="option"
                  aria-selected={active}
                  onMouseDown={(e) => {
                    // mousedown — blur 이전에 처리되어 클릭이 안 먹는 문제 방지
                    e.preventDefault();
                    pickSuggestion(s);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`px-3 py-1.5 cursor-pointer ${
                    active
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {highlightMatch(s, text.trim(), active)}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {!live && (
        <button
          type="button"
          onClick={() => submit()}
          className="btn btn-dark"
        >
          검색
        </button>
      )}
    </div>
  );
}

/** 후보 텍스트에서 매칭 부분을 굵게/색강조 — 대소문자 무시 */
function highlightMatch(text: string, q: string, active: boolean) {
  if (q === '') return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q.toLowerCase());
  if (idx < 0) return text;
  const before = text.slice(0, idx);
  const hit = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <span
        className={`font-semibold ${
          active ? 'underline' : 'text-blue-600'
        }`}
      >
        {hit}
      </span>
      {after}
    </>
  );
}
